import { makeAutoObservable } from 'mobx';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { map, mergeMap } from 'rxjs/operators';
import { isOrderOpen } from './utils/mungingUtils/orderStatusFilter';
import { tacticMap } from '../../classes/tactics/tacticKeys';

export class TradePlanStore {
  root;
  positionStream;

  // map {4234234: {}}

  currentSetupInfoByTradePlanId = {};

  // map {4234234: {...tradePlan}, 23423423: {...tradePlan}}
  tradePlans = {};

  //map {'AMD': { 12341: 'Live', 234453: 'Simulated', 4555: 'Live'}}
  tradePlansBySymbol = {};
  // map of arrays {4234234: [12312, 123123], 23423423: [534534, 345345]}
  softOrderIdsByTradePlan = {};

  // map {12312: {...softOrder}, 123123: {...softOrder}, 534534: {...softOrder}}
  softOrdersById = {};

  // map {123213: {...position}, 45363: {...position}}
  // idea for optimization, instead of replacing object, just update necessary fields
  positions = {
    tradeUnafraid: {},
    tradier: {},
  };
  // map {123213: {...position}, 45363: {...position}}
  orders = {
    tradeUnafraid: {},
    tradier: {},
  };
  ordersByTradePlanId = {};
  // array [{...execution}, {...execution}, {...execution}], these messages come in one by one. All current days executions are sent on load, one by one.
  executions = [];

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.initializeStreams();
  }

  initializeStreams = () => {
    //trade plan stream
    const tradePlanStream = groupByFunction(
      'messageType',
      'tradePlans',
      this.root.tradePlanWebsocketSubject
    );
    const removeTradePlanStream = groupByFunction(
      'messageType',
      'removeTradePlan',
      this.root.tradePlanWebsocketSubject
    );
    tradePlanStream.subscribe(this.handleTradePlanMessage);
    removeTradePlanStream.subscribe(this.handleRemoveTradePlanMsg);
    // remove alert trade plan, so we can remove the related soft orders in this store

    const removeAlertStream = groupByFunction(
      'messageType',
      'removeAlertTradePlan',
      this.root.tradePlanWebsocketSubject
    );
    removeAlertStream.subscribe(this.handleRemoveAlertTradePlanStream);

    // soft orders stream
    groupByFunction(
      'messageType',
      'softOrder',
      this.root.websocketSubject
    ).subscribe((msg) => {
      this.handleSoftOrderMessage(msg);
    });

    groupByFunction(
      'messageType',
      'removeSoftOrder',
      this.root.websocketSubject
    ).subscribe(({ tacticId }) => {
      this.handleRemoveSoftOrderMessage(tacticId);
    });

    //positions stream
    this.positionStream = groupByFunction(
      'messageType',
      'positions',
      this.root.websocketSubject
    ).pipe(
      map((positionMessage) => positionMessage.positions.position),
      mergeMap((p) => p)
    );
    this.positionStream.subscribe((msg) => this.handlePositionMessage(msg));

    //orders stream
    this.orderStream = groupByFunction(
      'messageType',
      'orderStatus',
      this.root.websocketSubject
    ).pipe(
      map((orderMessage) => orderMessage.orders.order),
      mergeMap((p) => p)
    );
    this.orderStream.subscribe((msg) => this.handleOrderMessage(msg));

    //executions stream
    this.executionsStream = groupByFunction(
      'messageType',
      'execution',
      this.root.websocketSubject
    );
    this.executionsStream.subscribe((msg) => this.handleExecutionMessage(msg));
  };

  //MESSAGE HANDLERS
  handleTradePlanMessage = (msg) => {
    const tradePlans = msg.tradePlans;
    if (tradePlans) {
      for (const id in tradePlans) {
        const expandedState = this.tradePlans[id]
          ? this.tradePlans[id].expanded
          : false;
        this.tradePlans[id] = tradePlans[id];
        this.tradePlans[id].expanded = expandedState;
        if (tradePlans[id].inPosition && this.currentSetupInfoByTradePlanId[id])
          delete this.currentSetupInfoByTradePlanId[id];
        const symbol = tradePlans[id].symbol;
        if (this.tradePlansBySymbol[symbol]) {
          this.tradePlansBySymbol[symbol][id] = tradePlans[id].dataSource;
        } else {
          this.tradePlansBySymbol[symbol] = {
            [id]: tradePlans[id].dataSource,
          };
        }
      }
    }
  };

  handleRemoveTradePlanMsg = (msg) => {
    const id = msg.tradePlanId;
    if (this.tradePlans[id]) {
      const symbol = this.tradePlans[id].symbol;
      delete this.tradePlans[id];
      if (this.softOrderIdsByTradePlan[id]) {
        this.softOrderIdsByTradePlan[id].forEach(
          (softOrder) => delete this.softOrdersById[softOrder]
        );
        delete this.softOrderIdsByTradePlan[id];
      }
      if (this.currentSetupInfoByTradePlanId[id])
        delete this.currentSetupInfoByTradePlanId[id];
      if (this.tradePlansBySymbol[symbol][id])
        delete this.tradePlansBySymbol[symbol][id];
    }
  };

  handleRemoveAlertTradePlanStream = (msg) => {
    const id = msg.tradePlanId;
    if (this.softOrderIdsByTradePlan[id]) {
      this.softOrderIdsByTradePlan[id].forEach(
        (softOrder) => delete this.softOrdersById[softOrder]
      );
      delete this.softOrderIdsByTradePlan[id];
    }
    if (this.currentSetupInfoByTradePlanId[id])
      delete this.currentSetupInfoByTradePlanId[id];
  };

  handleSoftOrderMessage = (msg) => {
    const tradePlanId = msg.tradePlanId;
    const softOrderId = msg.value.tacticId;
    if (this.softOrderIdsByTradePlan[tradePlanId]) {
      if (!this.softOrderIdsByTradePlan[tradePlanId].includes(softOrderId)) {
        this.softOrderIdsByTradePlan[tradePlanId].push(softOrderId);
      }
    } else {
      this.softOrderIdsByTradePlan[tradePlanId] = [softOrderId];
    }
    if (this.softOrdersById[softOrderId]) {
      for (const key in msg) {
        this.softOrdersById[softOrderId][key] = msg[key];
      }
    } else this.softOrdersById[softOrderId] = msg;
    // currentSoftOrder handler
    //still need to finish the rest of the setupDetailParsers
    // maybe make icons for each type of filter and a passing/not passing version of each

    const tactic = msg.value.tactic;
    const category = tacticMap[tactic].category;
    if (category === 'Setup') {
      const detailParser = tacticMap[tactic].setupDetailParser;
      const setupDetails = detailParser(this.root, msg);
      if (this.currentSetupInfoByTradePlanId[tradePlanId]) {
        this.currentSetupInfoByTradePlanId[tradePlanId].setupDetails =
          setupDetails.details;
        this.currentSetupInfoByTradePlanId[tradePlanId].triggerPrice =
          setupDetails.triggerPrice;
      } else {
        this.currentSetupInfoByTradePlanId[tradePlanId] = {
          triggerPrice: setupDetails.triggerPrice,
          setupDetails: setupDetails.details,
          filters: {},
        };
      }
    }
    if (category === 'Setup Filter') {
      const filterStatus = tacticMap[tactic].filterStatusParser(this.root, msg);
      const filter = msg.value.tactic;
      if (this.currentSetupInfoByTradePlanId[tradePlanId]) {
        this.currentSetupInfoByTradePlanId[tradePlanId].filters[
          msg.value.tactic
        ] = filterStatus;
      } else {
        this.currentSetupInfoByTradePlanId[tradePlanId] = {
          triggerPrice: false,
          setupDetails: null,
          filters: {
            [filter]: filterStatus,
          },
        };
      }
    }
  };

  handleRemoveSoftOrderMessage = (tacticId) => {
    if (this.softOrdersById[tacticId]) {
      const tradePlanId = this.softOrdersById[tacticId].tradePlanId;
      delete this.softOrdersById[tacticId];
      const newArr = this.softOrderIdsByTradePlan[tradePlanId].filter(
        (id) => id !== tacticId
      );
      this.softOrderIdsByTradePlan[tradePlanId] = newArr;
    }
  };
  handleRemoveTuPosition = (msgId) => {
    delete this.positions.tradeUnafraid[msgId];
  };
  handleRemoveTradierPosition = (msgId) => {
    delete this.positions.tradier[msgId];
  };

  handlePositionMessage = (msg) => {
    if (!msg.tradePlanId) {
      if (this.positions.tradier[msg.id]) {
        if (msg.quantity <= 0) {
          this.handleRemoveTradierPosition(msg.id);
        } else {
          this.positions.tradier[msg.id] = {
            ...this.positions.tradier[msg.id],
            ...msg,
          };
        }
      } else {
        if (msg.quantity > 0) this.positions.tradier[msg.id] = msg;
      }
    } else {
      if (this.positions.tradeUnafraid[msg.id]) {
        if (msg.quantity <= 0) {
          this.handleRemoveTuPosition(msg.id);
        } else {
          this.positions.tradeUnafraid[msg.id] = {
            ...this.positions.tradeUnafraid[msg.id],
            ...msg,
          };
        }
      } else {
        if (msg.quantity > 0) this.positions.tradeUnafraid[msg.id] = msg;
      }
    }
  };

  handleOrderMessage = (msg) => {
    msg.id = msg.id.toString();
    const id = msg.id;
    if (!msg.tradePlanId) {
      this.orders.tradier[id] = msg;
    } else {
      this.orders.tradeUnafraid[id] = msg;
      if (this.ordersByTradePlanId[msg.tradePlanId])
        this.ordersByTradePlanId[msg.tradePlanId][id] = msg;
      else
        this.ordersByTradePlanId[msg.tradePlanId] = {
          [id]: msg,
        };
    }
  };
  handleExecutionMessage = (msg) => {
    this.executions.push(msg.execution);
  };
  // MAPPING FUNCTIONS
  tradePlanIdMappingFunction = (msg) => {
    return msg.tradePlans.tradePlan.map((plan) => plan.id);
  };

  tradePlanMappingFunction = (msg) => {
    return msg.tradePlans.tradePlan.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  };

  //GETTERS
  //trade plans

  get tradePlanIdsSortedByCreateTime() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      return (
        this.tradePlans[a].createTimeStamp - this.tradePlans[b].createTimeStamp
      );
    });
  }
  get tradePlanIdsSortedBySymbol() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      return this.tradePlans[a].symbol.localeCompare(this.tradePlans[b].symbol);
    });
  }
  get tradePlanIdsSortedByStatus() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      if (this.tradePlans[a].inPosition === this.tradePlans[b].inPosition)
        if (
          this.tradePlans[a].restartSettings.deactivated ===
          this.tradePlans[b].restartSettings.deactivated
        ) {
          return this.tradePlans[a].symbol.localeCompare(
            this.tradePlans[b].symbol
          );
        } else if (this.tradePlans[a].restartSettings.deactivated) return -1;
        else return -1;
      if (this.tradePlans[a].inPosition === false) return 1;
      else return -1;
    });
  }
  get tradePlanIdsSortedByTemplate() {
    const arr = [...Object.keys(this.tradePlans)];
    return arr.sort((a, b) => {
      const result = this.tradePlans[a].savedConfigurationTitle.localeCompare(
        this.tradePlans[b].savedConfigurationTitle
      );
      if (result === 0)
        return this.tradePlans[a].symbol.localeCompare(
          this.tradePlans[b].symbol
        );
      else return result;
    });
  }

  //positions
  get tuPositionList() {
    const positions = this.positions.tradeUnafraid;
    return Object.keys(positions);
  }

  get tradierPositionList() {
    return Object.keys(this.positions.tradier);
  }

  //orders
  get openTuOrderList() {
    return Object.values(this.orders.tradeUnafraid).reduce((acc, item) => {
      if (isOrderOpen(item.status, 'TRADIER')) {
        return [...acc, item.id];
      } else return acc;
    }, []);
  }
  get openTradierOrderList() {
    return Object.values(this.orders.tradeUnafraid).reduce((acc, item) => {
      if (isOrderOpen(item.status, 'TRADIER')) return [...acc, item.id];
      else return acc;
    }, []);
  }

  get tradeUnafraidOrderList() {
    return Object.keys(this.orders.tradeUnafraid);
  }
  get tradierOrderList() {
    return Object.keys(this.orders.tradier);
  }

  //executions

  get executionsSortByTime() {
    const arr = [...this.executions];
    return arr.sort((a, b) => {
      const aMillis = new Date(a.executionTime).getTime();
      const bMillis = new Date(b.executionTime).getTime();
      return bMillis - aMillis;
    });
  }

  // mutations

  flipExpandedState = (tradePlanId) => {
    this.tradePlans[tradePlanId].expanded =
      !this.tradePlans[tradePlanId].expanded;
  };

  expandAll = () => {
    Object.keys(this.tradePlans).forEach((id) => {
      this.tradePlans[id].expanded = true;
    });
  };
  collapseAll = () => {
    Object.keys(this.tradePlans).forEach((id) => {
      this.tradePlans[id].expanded = false;
    });
  };
}
